import React, { createContext, useContext, useState, useRef, useCallback } from 'react';
import { musicAPI } from '../services/api';

const AudioContext = createContext();

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within an AudioProvider');
  }
  return context;
};

export const AudioProvider = ({ children }) => {
  const [currentPlaylist, setCurrentPlaylist] = useState(null);
  const [currentMusic, setCurrentMusic] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [playlistLength, setPlaylistLength] = useState(0);
  const [volume, setVolume] = useState(0.5);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isTrackLoading, setIsTrackLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isShuffled, setIsShuffled] = useState(true);
  const [shuffledIndices, setShuffledIndices] = useState([]);

  const audioRef = useRef(null);
  const playlistRef = useRef(null);

  // Initialize audio element
  if (!audioRef.current) {
    audioRef.current = new Audio();
    audioRef.current.volume = volume;
  }

  const generateShuffledIndices = useCallback((length) => {
    const indices = Array.from({ length }, (_, i) => i);
    for (let i = indices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [indices[i], indices[j]] = [indices[j], indices[i]];
    }
    return indices;
  }, []);

  const loadAndPlayMusic = useCallback(async (musicId, showLoading = true) => {
    try {
      if (showLoading) {
        setIsLoading(true);
      } else {
        setIsTrackLoading(true);
      }
      setError(null);

      const audioSrc = musicAPI.stream(musicId);
      const token = localStorage.getItem('token');

      if (!token) {
        setError('Cần đăng nhập để phát nhạc');
        return;
      }

      const response = await fetch(audioSrc, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load audio');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      audioRef.current.src = url;
      audioRef.current.load();
      await audioRef.current.play();
    } catch (err) {
      console.error('Error loading music:', err);
      setError('Không thể tải nhạc');
    } finally {
      if (showLoading) {
        setIsLoading(false);
      } else {
        setIsTrackLoading(false);
      }
    }
  }, []);

  // Audio event handlers
  const setupAudioEvents = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handlePlay = () => {
      setIsPlaying(true);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    const handleError = (e) => {
      console.error('Audio error:', e);
      setError('Lỗi phát nhạc');
      setIsPlaying(false);
    };

    const handleEnded = () => {
      // Use a timeout to avoid dependency issues
      setTimeout(() => {
        if (playlistRef.current && currentIndex < playlistRef.current.length - 1) {
          const nextIndex = currentIndex + 1;
          const nextMusic = playlistRef.current[nextIndex];
          const musicId = nextMusic.music_id || nextMusic.id;
          
          setCurrentIndex(nextIndex);
          setCurrentMusic(nextMusic);
          loadAndPlayMusic(musicId, false); // Don't show loading for auto-advance
        } else {
          setIsPlaying(false);
          setCurrentTime(0);
        }
      }, 100);
    };

    // Remove existing listeners
    audio.removeEventListener('timeupdate', handleTimeUpdate);
    audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
    audio.removeEventListener('ended', handleEnded);
    audio.removeEventListener('play', handlePlay);
    audio.removeEventListener('pause', handlePause);
    audio.removeEventListener('error', handleError);

    // Add new listeners
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('error', handleError);
  }, [currentIndex, loadAndPlayMusic]);

  // Setup audio events on mount
  React.useEffect(() => {
    setupAudioEvents();
  }, [setupAudioEvents]);

  const playPlaylist = useCallback(async (playlist) => {
    try {
      // Get playlist details if not already loaded
      let playlistData = playlist;
      let musicList = playlist.music || [];
      
      if (!playlist.music) {
        const response = await musicAPI.getPlaylist(playlist.id);
        // The API returns { playlist: {...}, music: [...] }
        playlistData = response.data.playlist;
        musicList = response.data.music || [];
      } else {
        // If playlist already has music, ensure we have the correct structure
        // The playlist object should have the name property directly
        playlistData = {
          id: playlist.id,
          name: playlist.name,
          description: playlist.description,
          created_by: playlist.created_by,
          creator_username: playlist.creator_username,
          is_admin_playlist: playlist.is_admin_playlist,
          created_at: playlist.created_at,
          updated_at: playlist.updated_at,
          music_count: playlist.music_count
        };
      }

      if (musicList.length === 0) {
        setError('Playlist trống');
        return;
      }

      setCurrentPlaylist(playlistData);
      setCurrentIndex(0);
      setPlaylistLength(musicList.length);
      playlistRef.current = musicList;

      // Generate shuffled indices if shuffle is enabled
      if (isShuffled) {
        const shuffled = generateShuffledIndices(musicList.length);
        setShuffledIndices(shuffled);
      } else {
        setShuffledIndices([]);
      }

      // Play first song (or first shuffled song)
      const firstIndex = isShuffled ? shuffledIndices[0] || 0 : 0;
      const firstMusic = musicList[firstIndex];
      const musicId = firstMusic.music_id || firstMusic.id;
      setCurrentMusic(firstMusic);
      setCurrentIndex(firstIndex);
      await loadAndPlayMusic(musicId);
    } catch (err) {
      console.error('Error playing playlist:', err);
      setError('Không thể phát playlist');
    }
  }, [loadAndPlayMusic, isShuffled, shuffledIndices, generateShuffledIndices]);

  const handlePlayPause = useCallback(async () => {
    if (!audioRef.current) return;

    try {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        await audioRef.current.play();
      }
    } catch (err) {
      console.error('Error in play/pause:', err);
      setError('Lỗi điều khiển phát nhạc');
    }
  }, [isPlaying]);

  const handleNext = useCallback(async () => {
    console.log('handleNext called, currentIndex:', currentIndex, 'playlistLength:', playlistRef.current?.length);
    if (!playlistRef.current) {
      setIsPlaying(false);
      setCurrentTime(0);
      return;
    }

    let nextIndex;
    if (isShuffled && shuffledIndices.length > 0) {
      // Find current position in shuffled array
      const currentShuffledIndex = shuffledIndices.indexOf(currentIndex);
      if (currentShuffledIndex >= shuffledIndices.length - 1) {
        // End of shuffled playlist
        setIsPlaying(false);
        setCurrentTime(0);
        return;
      }
      nextIndex = shuffledIndices[currentShuffledIndex + 1];
    } else {
      // Normal sequential playback
      if (currentIndex >= playlistRef.current.length - 1) {
        // End of playlist
        setIsPlaying(false);
        setCurrentTime(0);
        return;
      }
      nextIndex = currentIndex + 1;
    }

    const nextMusic = playlistRef.current[nextIndex];
    const musicId = nextMusic.music_id || nextMusic.id;

    setCurrentIndex(nextIndex);
    setCurrentMusic(nextMusic);
    await loadAndPlayMusic(musicId, false); // Don't show loading for track changes
  }, [currentIndex, loadAndPlayMusic, isShuffled, shuffledIndices]);

  const handlePrevious = useCallback(async () => {
    console.log('handlePrevious called, currentIndex:', currentIndex, 'playlistLength:', playlistRef.current?.length);
    if (!playlistRef.current) {
      return;
    }

    let prevIndex;
    if (isShuffled && shuffledIndices.length > 0) {
      // Find current position in shuffled array
      const currentShuffledIndex = shuffledIndices.indexOf(currentIndex);
      if (currentShuffledIndex <= 0) {
        return; // Already at the beginning
      }
      prevIndex = shuffledIndices[currentShuffledIndex - 1];
    } else {
      // Normal sequential playback
      if (currentIndex <= 0) {
        return;
      }
      prevIndex = currentIndex - 1;
    }

    const prevMusic = playlistRef.current[prevIndex];
    const musicId = prevMusic.music_id || prevMusic.id;

    setCurrentIndex(prevIndex);
    setCurrentMusic(prevMusic);
    await loadAndPlayMusic(musicId, false); // Don't show loading for track changes
  }, [currentIndex, loadAndPlayMusic, isShuffled, shuffledIndices]);

  const handleSeek = useCallback((newTime) => {
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  }, []);

  const handleVolumeChange = useCallback((newVolume) => {
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  }, []);

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsPlaying(false);
    setCurrentTime(0);
    setCurrentPlaylist(null);
    setCurrentMusic(null);
    setCurrentIndex(0);
    setPlaylistLength(0);
    setError(null);
    setShuffledIndices([]);
    setIsTrackLoading(false);
  }, []);

  const toggleShuffle = useCallback(() => {
    setIsShuffled(prev => !prev);
    if (currentPlaylist && playlistRef.current) {
      if (!isShuffled) {
        // Enable shuffle - generate new shuffled order
        const shuffled = generateShuffledIndices(playlistRef.current.length);
        setShuffledIndices(shuffled);
      } else {
        // Disable shuffle - clear shuffled indices
        setShuffledIndices([]);
      }
    }
  }, [isShuffled, currentPlaylist, generateShuffledIndices]);

  const isCurrentPlaylist = useCallback((playlistId) => {
    return currentPlaylist?.id === playlistId;
  }, [currentPlaylist]);

  const value = {
    // State
    currentPlaylist,
    currentMusic,
    isPlaying,
    currentIndex,
    playlistLength,
    volume,
    currentTime,
    duration,
    isLoading,
    error,
    isTrackLoading,
    isShuffled,
    
    // Actions
    playPlaylist,
    handlePlayPause,
    handleNext,
    handlePrevious,
    handleSeek,
    handleVolumeChange,
    stop,
    toggleShuffle,
    isCurrentPlaylist,
  };

  return (
    <AudioContext.Provider value={value}>
      {children}
    </AudioContext.Provider>
  );
};